import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LanguageLayout from './i18n/LanguageLayout';

// Product.jsx, Platform.jsx e WhyAitrack.jsx NON sono più instradate (decisione founder
// 27/07: contenuto consolidato in home; dal 17/09/2026 tutte rimandano a #piattaforma).
// I file restano sul disco (mai cancellare file), semplicemente non più importati qui.
// Stessa sorte, il 16/09/2026, per FeaturePage.jsx: le schede /piattaforma/:slug sono
// ritirate (founder: «non le usiamo») e i loro URL portano alla panoramica della home.
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Resources = lazy(() => import('./pages/Resources'));
const GetStarted = lazy(() => import('./pages/GetStarted'));
const Privacy = lazy(() => import('./pages/Privacy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const Support = lazy(() => import('./pages/Support'));
const Login = lazy(() => import('./pages/Login'));
const NotFound = lazy(() => import('./pages/NotFound'));
const CookieBanner = lazy(() => import('./components/CookieBanner'));
const ScriptGate = lazy(() => import('./components/ScriptGate'));

function PageLoader() {
  return (
    <div role="status" aria-label="Caricamento pagina" style={{
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--canvas)',
    }}>
      <div style={{
        width: 40,
        height: 40,
        border: '3px solid rgba(0,0,0,0.08)',
        borderTopColor: 'var(--brand)',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
    </div>
  );
}

// Route figlie condivise tra i rami lingua (IT su /, EN su /en).
function childRoutes(prefix) {
  // Home del ramo lingua corrente, per i redirect delle vecchie pagine consolidate.
  const homePath = prefix === 'en' ? '/en' : '/';
  return [
    <Route key={`${prefix}-index`} index element={<Home />} />,
    <Route key={`${prefix}-about`} path="about" element={<About />} />,
    // Redirect 27/07: /product, /platform, /why-aitrack non esistono più come pagine
    // (contenuto consolidato in home). Vecchi URL potenzialmente indicizzati → redirect
    // alla sezione home corrispondente, non 404.
    <Route key={`${prefix}-product`} path="product" element={<Navigate to={{ pathname: homePath, hash: '#piattaforma' }} replace />} />,
    <Route key={`${prefix}-platform`} path="platform" element={<Navigate to={{ pathname: homePath, hash: '#piattaforma' }} replace />} />,
    <Route key={`${prefix}-resources`} path="resources" element={<Resources />} />,
    /* Nuova rotta 13/08: guida "Guida per iniziare" — stesso slug per IT/EN
       (il router non supporta slug localizzati per lingua senza riscrivere lo
       schema childRoutes: qui sotto la stessa path "come-iniziare" risponde su
       "/come-iniziare" (IT) e "/en/come-iniziare" (EN), non "/en/getting-started"). */
    <Route key={`${prefix}-getstarted`} path="come-iniziare" element={<GetStarted />} />,
    <Route key={`${prefix}-why`} path="why-aitrack" element={<Navigate to={{ pathname: homePath, hash: '#piattaforma' }} replace />} />,
    <Route key={`${prefix}-contact`} path="contact" element={<Contact />} />,
    <Route key={`${prefix}-support`} path="support" element={<Support />} />,
    <Route key={`${prefix}-assistenza`} path="assistenza" element={<Support />} />,
    <Route key={`${prefix}-login`} path="login" element={<Login />} />,
    /* Demo ritirata (founder 13/08/2026, stessa scelta marketing-first della
       Roadmap): redirect a home, non 404, per i link già indicizzati. Componente
       Demo.jsx CONSERVATO su disco (non instradato, non cancellato). */
    <Route key={`${prefix}-demo`} path="demo" element={<Navigate to={homePath} replace />} />,
    /* Roadmap ritirata (founder 12/08/2026): redirect, non 404, per i link già indicizzati. */
    <Route key={`${prefix}-roadmap`} path="roadmap" element={<Navigate to={homePath} replace />} />,
    <Route key={`${prefix}-privacy`} path="privacy" element={<Privacy />} />,
    <Route key={`${prefix}-cookie`} path="cookie-policy" element={<CookiePolicy />} />,
    <Route key={`${prefix}-terms`} path="terms" element={<Terms />} />,
    /* Catalogo e schede funzione ritirati (16/09/2026): pagina /piattaforma, schede
       /piattaforma/:slug e i vecchi /features, /features/:slug portano tutti alla
       panoramica della home. Il 301 vero lo dà public/_redirects: questo è il ripiego
       per la navigazione interna e per l'anteprima locale. */
    ...['piattaforma', 'piattaforma/:slug', 'features', 'features/:slug'].map((path) => (
      <Route key={`${prefix}-${path}`} path={path} element={<Navigate to={{ pathname: homePath, hash: '#piattaforma' }} replace />} />
    )),
    <Route key={`${prefix}-404`} path="*" element={<NotFound />} />,
  ];
}

function App() {
  const location = useLocation();

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      document.getElementById('main-content')?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  useEffect(() => {
    // Nessun hash: scroll ISTANTANEO in cima ad ogni cambio pagina (senza animazione).
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      return undefined;
    }

    // Con hash (es. redirect /platform → /#piattaforma, o link footer ad ancora
    // home): la sezione target può non essere ancora montata (Home è lazy/Suspense),
    // quindi si ritenta per qualche frame finché l'elemento non esiste.
    const id = decodeURIComponent(location.hash.slice(1));
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    };
    if (tryScroll()) return undefined;

    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (tryScroll() || attempts > 30) clearInterval(interval);
    }, 100);
    return () => clearInterval(interval);
  }, [location.pathname, location.hash]);

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* EN — path-prefix /en */}
          <Route path="/en" element={<LanguageLayout lang="en" />}>
            {childRoutes('en')}
          </Route>
          {/* IT — default, nessun prefisso */}
          <Route path="/" element={<LanguageLayout lang="it" />}>
            {childRoutes('it')}
          </Route>
        </Routes>
      </Suspense>
      <CookieBanner />
      <ScriptGate />
    </>
  );
}

export default App;
